import React, { FC, useState } from 'react';
import { Autocomplete, Box, TextField, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@apollo/client';
import { getTechniciansForUserListTemplate } from 'apollo/templates/user/query.graphql';
import { useDenthubErrorBoundary } from 'components/shared/denthub-error-boundary/denthub-error-boundary-context';
import InfoText from 'components/shared/info-text.component';
import { User } from 'models/user';

type SubcontractResponsibleUsersProps = {
    userIds: string[];
    onChange: (userIds: string[]) => void;
};

/**
 * DD-3689: the lab's users offered as responsible for a sub-order to this supplier. With one
 * user, the subcontract flow pre-fills them; with none, every technician of the lab is offered.
 */
const SubcontractResponsibleUsers: FC<SubcontractResponsibleUsersProps> = ({ userIds, onChange }) => {
    const { t: translate } = useTranslation(['orders']);
    const { showBoundary } = useDenthubErrorBoundary();
    const [labUsers, setLabUsers] = useState<User[]>([]);

    const { loading } = useQuery(getTechniciansForUserListTemplate, {
        variables: { filters: { active: true, role: 'technician' }, limit: 0, offset: 0 },
        onCompleted: ({ technicians }) => setLabUsers(technicians),
        onError(error) {
            showBoundary(error);
        },
    });

    const selectedUsers = labUsers.filter((user) => userIds.includes(user._id));

    return (
        <Box mb={2}>
            <Typography variant="h3" mb={1}>
                {translate('orders:SUBCONTRACT_RESPONSIBLE_USERS')}
            </Typography>
            <InfoText text={translate('orders:SUBCONTRACT_RESPONSIBLE_USERS_INFO')} />
            <Box mt={1} maxWidth={480}>
                <Autocomplete
                    multiple
                    options={labUsers}
                    value={selectedUsers}
                    loading={loading}
                    onChange={(_event, users) => onChange(users.map((user) => user._id))}
                    isOptionEqualToValue={(option, value) => option._id === value._id}
                    getOptionLabel={(option) => option.fullName || ''}
                    renderInput={(inputParams) => (
                        <TextField
                            {...inputParams}
                            label={translate('orders:RESPONSIBLE_USER')}
                            placeholder={userIds.length ? undefined : translate('orders:SUBCONTRACT_ALL_TECHNICIANS')}
                            data-test="subcontract-defaults.responsible-users"
                        />
                    )}
                />
            </Box>
        </Box>
    );
};

export default SubcontractResponsibleUsers;
